// Eltern-Portal: Profil aus club_people + trainerstunden_billing_contacts (SSoT je Rolle, gleiche id).

import { getSupabaseClient } from "../supabase/index.js";
import { CLUB_PERSON_CONTACT_SOURCE_BILLING } from "../masterdata/contact-source.js";
import { normalizeParentPortalProfileRow } from "./parent-portal-profile.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

function __pickNonEmpty(...vals) {
  for (const v of vals) {
    const s = __asStr(v);
    if (s) return s;
  }
  return "";
}

/**
 * Vereint Spiegel (club_people) und Rechnungsempfänger-Stammdaten (billing_contacts).
 * @param {object|null|undefined} cp
 * @param {object|null|undefined} bc
 * @param {object|null|undefined} tp
 */
export function mergeParentPortalProfileFromSsotRows(cp, bc, tp) {
  const row = cp && typeof cp === "object" ? cp : {};
  const billing = bc && typeof bc === "object" ? bc : {};
  const ttb = tp && typeof tp === "object" ? tp : {};
  const isBillingMirror =
    __asStr(row.contact_source) === CLUB_PERSON_CONTACT_SOURCE_BILLING || Boolean(billing.id);

  const nameOrder = isBillingMirror
    ? [
        [billing.first_name, row.first_name, ttb.first_name],
        [billing.last_name, row.last_name, ttb.last_name],
      ]
    : [
        [row.first_name, billing.first_name, ttb.first_name],
        [row.last_name, billing.last_name, ttb.last_name],
      ];

  const contactOrder = isBillingMirror
    ? [billing.email, row.email, ttb.email]
    : [row.email, billing.email, ttb.email];

  const phoneOrder = isBillingMirror
    ? [row.phone, billing.phone, ttb.phone]
    : [row.phone, billing.phone, ttb.phone];

  const addressPick = (field) => {
    const fromRow = row[field];
    const fromBilling = billing[field];
    return __pickNonEmpty(fromRow, fromBilling);
  };

  const id = __pickNonEmpty(row.id, billing.id, ttb.id);
  const birthDate = row.birth_date ?? ttb.birth_date ?? null;
  const birthYear = row.birth_year ?? ttb.birth_year ?? "";

  return {
    id,
    firstName: __pickNonEmpty(...nameOrder[0]),
    lastName: __pickNonEmpty(...nameOrder[1]),
    email: __pickNonEmpty(...contactOrder),
    phone: __pickNonEmpty(...phoneOrder),
    birthDate: birthDate ? String(birthDate).slice(0, 10) : "",
    birthYear,
    street: addressPick("street"),
    houseNumber: addressPick("house_number"),
    postalCode: addressPick("postal_code"),
    city: addressPick("city"),
    country: __pickNonEmpty(addressPick("country"), "DE"),
    emailEditable: false,
    isSelfPerson: false,
    hasRegisteredGuardianRelation: false,
    criticalChangesRequireApproval: false,
    pendingCriticalChange: null,
  };
}

async function __loadParentPortalProfileFromSsotTablesRemote(pid) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const [cpR, bcR, tpR] = await Promise.all([
    supabase.from("club_people").select("*").eq("id", pid).maybeSingle(),
    supabase.from("trainerstunden_billing_contacts").select("*").eq("id", pid).maybeSingle(),
    supabase.from("trainerstunden_people").select("*").eq("id", pid).maybeSingle(),
  ]);

  if (cpR.error && bcR.error) {
    return { ok: false, reason: "load-failed", error: cpR.error || bcR.error };
  }

  const merged = mergeParentPortalProfileFromSsotRows(cpR.data, bcR.data, tpR.data);
  if (!__asStr(merged.id)) {
    return { ok: false, reason: "person-not-found" };
  }

  if (
    !__pickNonEmpty(merged.firstName, merged.lastName, merged.email, merged.street, merged.phone, merged.city)
  ) {
    return { ok: false, reason: "person-not-found" };
  }

  return { ok: true, profile: normalizeParentPortalProfileRow(merged) };
}

/**
 * Liest Profil aus SSoT-Tabellen (RPC security definer, Fallback direkte SELECT).
 * @param {string} personId
 */
export async function loadParentPortalProfileFromSsotRemote(personId) {
  const pid = __asStr(personId);
  if (!pid) return { ok: false, reason: "missing-person-id" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc("trainerstunden_parent_portal_get_profile_ssot", {
      p_person_id: pid,
    });

    if (!error && data?.ok === true && data?.profile) {
      return { ok: true, profile: normalizeParentPortalProfileRow(data.profile) };
    }

    const code = __asStr(error?.code);
    if (error && code !== "PGRST202") {
      return { ok: false, reason: "rpc-error", error };
    }

    return await __loadParentPortalProfileFromSsotTablesRemote(pid);
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}
